import * as React from 'react';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import GlobalModal from '../../components/GlobalModal';
import DisplayDateTime from '../../components/DisplayDateTime';
import ActivityFeedModal, { ViewTypes } from './ActivityFeedModal';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { DomainProfileType, DashboardActivityFeedActivity } from '../../api/models';

interface ActivityFeedTypes {
	activityFeedData: DashboardActivityFeedActivity[];
	getActivityFeed: () => Promise<void>;
	isLoading: boolean;
	activityFeedFormErrorMessage: string;
}

export const ActivityFeed: React.FC<ActivityFeedTypes> = ({
	activityFeedData,
	getActivityFeed,
	isLoading,
	activityFeedFormErrorMessage
}) => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Check Profile Type of the Active User
	const activeProfileType = useAppSelector((state) => state.user.activeProfileType);

	// State to control Modals
	const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);

	const [showReadModal, setShowReadModal] = React.useState<boolean>(false);

	const [modalData, setModalData] = React.useState<DashboardActivityFeedActivity>();

	const createItem = () => {
		setShowReadModal(false);

		setShowCreateModal(true);
	};

	const readItem = (index: number) => {
		const item = activityFeedData[index];

		setModalData(item);

		setShowCreateModal(false);

		setShowReadModal(true);
	};

	return (
		<aside className="feed-list">
			{!!activityFeedFormErrorMessage ? (
				<div className="form-validation-error">{activityFeedFormErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<header>
									<h1 className="h4">Activity Feed</h1>

									{isOrganisationActive &&
										(activeProfileType === DomainProfileType.SuperAdministrator ||
											activeProfileType === DomainProfileType.Staff) && (
											<Button className="btn-secondary small" buttonIcon={<PlusIcon />} onClick={createItem}>
												Add Activity
											</Button>
										)}
								</header>

								<ul>
									{activityFeedData &&
										activityFeedData.length > 0 &&
										activityFeedData.map((item, index) => (
											<li key={item.id}>
												<div>
													<p>{item.title}</p>
													<time>
														<DisplayDateTime>{item.date}</DisplayDateTime>
													</time>
												</div>

												<Button className="btn-icon" buttonIcon={<PlusIcon />} onClick={() => readItem(index)}></Button>
											</li>
										))}
								</ul>

								<GlobalModal isOpen={showCreateModal} setIsOpen={setShowCreateModal} titleText="Log an activity">
									<ActivityFeedModal viewType={ViewTypes.Create} refreshActivityFeed={getActivityFeed} />
								</GlobalModal>

								<GlobalModal isOpen={showReadModal} setIsOpen={setShowReadModal} titleText="Activity Feed">
									<ActivityFeedModal
										viewType={ViewTypes.Read}
										activity={modalData}
										refreshActivityFeed={getActivityFeed}
									/>
								</GlobalModal>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</aside>
	);
};

export default ActivityFeed;
